/**
 * Employee API Service
 * Handles all employee-related API calls
 */

import { apiClient } from '../client';
import type { Employee, EmployeeCreate } from '../../types/api';

export const employeesApi = {
  /**
   * Get all employees with optional filters
   */
  getAll: async (params?: {
    department?: string;
    work_location?: string;
    employment_status?: string;
  }): Promise<{ employees: Employee[]; total: number; summary: any }> => {
    const response = await apiClient.get('/employees/', { params });
    return response.data;
  },

  /**
   * Get employee by ID
   */
  getById: async (id: number): Promise<Employee> => {
    const response = await apiClient.get<Employee>(`/employees/${id}`);
    return response.data;
  },

  /**
   * Create new employee
   */
  create: async (data: EmployeeCreate): Promise<{ employee: Employee; message: string }> => {
    const response = await apiClient.post('/employees/', data);
    return response.data;
  },

  /**
   * Update existing employee
   */
  update: async (id: number, data: Partial<EmployeeCreate>): Promise<{ employee: Employee; message: string }> => {
    const response = await apiClient.put(`/employees/${id}`, data);
    return response.data;
  },

  /**
   * Delete employee (soft delete)
   */
  delete: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/employees/${id}`);
    return response.data;
  },

  /**
   * Get employee payroll records
   */
  getPayroll: async (id: number, year?: number): Promise<any> => {
    const response = await apiClient.get(`/employees/${id}/payroll`, {
      params: { year },
    });
    return response.data;
  },

  /**
   * Create payroll record
   */
  createPayroll: async (data: any): Promise<any> => {
    const response = await apiClient.post('/employees/payroll', data);
    return response.data;
  },

  /**
   * Process payroll for multiple employees
   */
  processPayroll: async (data: any): Promise<any> => {
    const response = await apiClient.post('/employees/payroll/process', data);
    return response.data;
  },

  /**
   * Get current UK tax rates and thresholds
   */
  getTaxRates: async (): Promise<any> => {
    const response = await apiClient.get('/employees/payroll/rates');
    return response.data;
  },
};

export default employeesApi;
